import {
  ExchangeName,
  ExternalAccountType,
  ConfigurationExchangeInfo,
  UserSource,
  AuthUser,
  StorageOAuthData,
  StorageOauthUserData,
  DisplayCurrency,
  LanguageNameSpaces,
} from "@shared/types";
import { cloneDeep } from "lodash";
import {
  DISPLAY_CURRENCIES,
  STORAGE_OAUTH_DATA_KEY,
  STORAGE_OAUTH_TEMP_DATA_KEY,
} from "@shared/common/constants";
import { sharedLib } from "@shared/services/shared.service";
import { TradingMethodType } from "@shared/features/onboarding/types";
import {
  SKIP_ONBOARDING_FOLLOW_GROUP,
  SKIP_ONBOARDING_SIGNALS_BOT_HAS_GROUPS,
} from "@shared/features/onboarding/constants";
import { TranslationKeys } from "@shared/common/i18n/types";

export const getSignUpLink = (
  exchange?: string,
  exchangesList?: ConfigurationExchangeInfo[]
) => {
  return exchangesList?.find(
    (exchangeInfo) => exchangeInfo.nested_exchange === exchange
  )?.sign_up_link;
};

export const setExpandedArray = (
  expanded: boolean[],
  setExpanded: (expanded: boolean[]) => void,
  index: number,
  value: boolean
) => {
  let tempArray = cloneDeep(expanded);
  tempArray[index] = value;
  setExpanded(tempArray);
};

export const getExternalAccountTypeDisplay = (
  externalAccountType?: ExternalAccountType
) => {
  if (externalAccountType === ExternalAccountType.TELEGRAM) return "Telegram";
  else if (externalAccountType === ExternalAccountType.DISCORD)
    return "Discord";
  return "External";
};

export const getQuickConnectGuideList = (
  exchange: ExchangeName | undefined,
  t: (
    key: TranslationKeys<LanguageNameSpaces.PORTFOLIO>,
    params?: any
  ) => string
) => {
  return [
    t("quick_connect.guide_step_1", { exchange }),
    t("quick_connect.guide_step_2", { exchange }),
    t("quick_connect.guide_step_3"),
  ];
};

export const getCreateSingleAccountSuccessMessage = (
  exchange: ExchangeName,
  t: (key: TranslationKeys<LanguageNameSpaces.COMMON>, params?: any) => string
) => {
  return t("drawers.account_messages.create_success", { exchange });
};

export const getEditAccountSuccessMessage = (
  exchange: ExchangeName,
  t: (key: TranslationKeys<LanguageNameSpaces.COMMON>, params?: any) => string
) => {
  return t("drawers.account_messages.edit_success", { exchange });
};

export const getCreateAccountsSuccessMessage = (
  numOfExchanges: number,
  t: (key: TranslationKeys<LanguageNameSpaces.COMMON>, params?: any) => string
) => {
  return t("drawers.account_messages.create_accounts_success", {
    numOfExchanges,
  });
};

export const getCreateOrEditAccountErrorMessage = (
  action: string,
  t: (key: TranslationKeys<LanguageNameSpaces.COMMON>, params?: any) => string
) => {
  return t("drawers.account_messages.create_error", { action });
};

export const getCreateApiLink = (
  exchange?: string,
  exchangesList?: ConfigurationExchangeInfo[]
) => {
  return exchangesList?.find(
    (subExchange) => subExchange.nested_exchange === exchange
  )?.info_link;
};

export const getUserSourceFromRef = (ref?: string): UserSource => {
  if (ref === "telegram") return UserSource.TELEGRAM;
  if (ref === "discord") return UserSource.DISCORD;
  return UserSource.DIRECT;
};

export const getSharedOnboardingUserSource = (storageOnboardingData: any) => {
  let ref: string | undefined;
  try {
    const parsedRegistrationData = storageOnboardingData
      ? JSON.parse(storageOnboardingData)
      : undefined;
    ref = parsedRegistrationData?.ref;
  } catch {}
  return getUserSourceFromRef(ref);
};

export const handleUserTempOAuthData = (userId?: number) => {
  if (!userId) return;
  const userOauthData: StorageOauthUserData | undefined =
    sharedLib.storageService.getJsonValue(STORAGE_OAUTH_TEMP_DATA_KEY);
  if (userOauthData) {
    sharedLib.storageService.setJsonKeyValue(
      STORAGE_OAUTH_DATA_KEY,
      userId.toString(),
      userOauthData
    );
    sharedLib.storageService.removeValue(STORAGE_OAUTH_TEMP_DATA_KEY);
  }
  return !!userOauthData;
};

export const getUserOauthData = (user?: AuthUser) => {
  if (!user) return;
  const oauthData: StorageOAuthData | undefined =
    sharedLib.storageService.getJsonValue(STORAGE_OAUTH_DATA_KEY);
  return oauthData?.[user.id];
};

export const changeSelectedItemPosition = (
  list: any[],
  selectedItemId: any,
  maxItems: number
) => {
  let tempList = [...list];
  const index = tempList.findIndex((item) => item?.id === selectedItemId);
  if (index >= maxItems) {
    const object = list[index];
    tempList.splice(index, 1);
    tempList.unshift(object);
  }
  return tempList;
};

export const getDisplayCurrencyPrecision = (
  displayCurrency: DisplayCurrency
) => {
  return displayCurrency === DISPLAY_CURRENCIES.BTC ? 5 : 2;
};

export const getOverviewSkipOnboardingButtonText = (
  tradingMethod: TradingMethodType
) => {
  let action = "create a DCA bot";
  if (tradingMethod === TradingMethodType.SIGNALS_GROUP) {
    action = "create a signals bot";
  }
  return `I don’t want to ${action} right now`;
};

export const getSkipOnboardingFollowGroupModalContent = (
  user: AuthUser | undefined,
  t: (key: TranslationKeys<LanguageNameSpaces.ONBOARDING>) => string
) => {
  if (user?.hasExternalAccount() && user?.has_groups) {
    return t(SKIP_ONBOARDING_SIGNALS_BOT_HAS_GROUPS);
  } else return t(SKIP_ONBOARDING_FOLLOW_GROUP);
};

export const getDisplayUrl = (url?: string) => url?.replace("https://www.", "");

export const updateStoreUserIsOnboarding = (isOnboarding: boolean) => {
  return sharedLib.slices.authentication.actions.updateUser({
    key: "is_onboarding",
    value: isOnboarding,
  });
};
